import React from "react";
import { colors, fonts } from "../utils/constants.js";
import { isMobile, getButtonStyle } from "../utils/responsive.js";

function EnhancedMarketRow({ 
  market, 
  onVote, 
  onViewDetails, 
  userVote = null, 
  isGuest = false 
}) {
  const [isMobileDevice, setIsMobileDevice] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState(null);

  React.useEffect(() => {
    const checkIsMobile = () => setIsMobileDevice(isMobile());
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const marketType = market.market_type || 'binary';
  const isResolved = market.status === "final";
  const hasVoted = !!userVote;

  // Category styling
  const getCategoryIcon = (category) => {
    const icons = {
      politics: "🏛️",
      economy: "💰", 
      sports: "⚽",
      culture: "🎭",
      technology: "💻",
      demographics: "👥",
      environment: "🌱",
      international: "🌍",
      entertainment: "🎬",
      science: "🔬"
    };
    return icons[category] || "📊";
  };

  const getCategoryColor = (category) => {
    const colors = {
      politics: "#3B82F6",
      economy: "#10B981",
      sports: "#F59E0B", 
      culture: "#8B5CF6",
      technology: "#06B6D4",
      demographics: "#84CC16",
      environment: "#22C55E",
      international: "#EF4444",
      entertainment: "#F97316",
      science: "#6366F1"
    };
    return colors[category] || "#6B7280";
  };

  const handleVote = (voteData) => {
    if (hasVoted || isResolved) return;
    onVote(voteData);
  };

  const renderBinaryMarket = () => {
    const yesPercentage = Math.round(market.yes_probability * 100);
    const noPercentage = 100 - yesPercentage;

    return (
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        {/* Binary voting buttons */}
        <div style={{ display: "flex", gap: 8, flex: 1 }}>
          <button
            onClick={() => handleVote({ side: "YES" })}
            disabled={hasVoted || isResolved}
            style={{
              ...getButtonStyle("secondary", "small", isMobileDevice),
              flex: 1,
              background: hasVoted && userVote?.side === "YES" ? colors.green : 
                         hasVoted || isResolved ? colors.border : "transparent",
              color: hasVoted && userVote?.side === "YES" ? "#FFF" : colors.green,
              border: `1px solid ${colors.green}`,
              cursor: hasVoted || isResolved ? "not-allowed" : "pointer",
              opacity: hasVoted || isResolved ? 0.6 : 1,
            }}
          >
            ДА {yesPercentage}%
          </button>
          <button
            onClick={() => handleVote({ side: "NO" })}
            disabled={hasVoted || isResolved}
            style={{
              ...getButtonStyle("secondary", "small", isMobileDevice),
              flex: 1,
              background: hasVoted && userVote?.side === "NO" ? colors.red : 
                         hasVoted || isResolved ? colors.border : "transparent",
              color: hasVoted && userVote?.side === "NO" ? "#FFF" : colors.red,
              border: `1px solid ${colors.red}`,
              cursor: hasVoted || isResolved ? "not-allowed" : "pointer",
              opacity: hasVoted || isResolved ? 0.6 : 1,
            }}
          >
            НЕ {noPercentage}%
          </button>
        </div>
      </div>
    );
  };

  const renderCategoricalMarket = () => {
    const options = market.options || [];
    const topOptions = options.slice(0, isMobileDevice ? 2 : 3);
    const hasMore = options.length > topOptions.length;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {/* Top options */}
        <div style={{ 
          display: "flex", 
          gap: 6, 
          flexWrap: isMobileDevice ? "wrap" : "nowrap" 
        }}>
          {topOptions.map((option, index) => (
            <button
              key={option.id}
              onClick={() => handleVote({ option_id: option.id })}
              disabled={hasVoted || isResolved}
              style={{
                ...getButtonStyle("secondary", "small", isMobileDevice),
                flex: isMobileDevice && index >= 2 ? "1" : "auto",
                minWidth: isMobileDevice ? "auto" : "80px",
                background: hasVoted && userVote?.option_id === option.id ? colors.blue : 
                           hasVoted || isResolved ? colors.border : "transparent",
                color: hasVoted && userVote?.option_id === option.id ? "#FFF" : colors.blue,
                border: `1px solid ${colors.blue}`,
                cursor: hasVoted || isResolved ? "not-allowed" : "pointer",
                opacity: hasVoted || isResolved ? 0.6 : 1,
                fontSize: isMobileDevice ? 11 : 12,
                padding: "6px 10px",
              }}
            >
              {option.option_text} {Math.round((option.probability || 0) * 100)}%
            </button>
          ))}
        </div>
        
        {/* More options indicator */}
        {hasMore && (
          <button
            onClick={onViewDetails}
            style={{
              background: "transparent",
              border: "none",
              color: colors.textSec,
              fontSize: 11,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            +{options.length - topOptions.length} още опции...
          </button>
        )}
      </div>
    );
  };

  const renderRangeMarket = () => {
    const options = market.options || [];
    
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {/* Range options as a horizontal bar */}
        <div style={{ 
          display: "flex", 
          height: 24,
          borderRadius: 12,
          overflow: "hidden",
          border: `1px solid ${colors.border}`,
        }}>
          {options.map((option, index) => (
            <button
              key={option.id}
              onClick={() => handleVote({ option_id: option.id })}
              disabled={hasVoted || isResolved}
              style={{
                flex: 1,
                background: hasVoted && userVote?.option_id === option.id ? colors.green :
                           `hsl(${120 - (index * 30)}, 60%, ${85 - (option.probability || 0) * 20}%)`,
                border: "none",
                cursor: hasVoted || isResolved ? "not-allowed" : "pointer",
                opacity: hasVoted || isResolved ? 0.6 : 1,
                fontSize: 10,
                fontWeight: 600,
                color: hasVoted && userVote?.option_id === option.id ? "#FFF" : colors.textSec,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
              }}
              title={`${option.option_text}: ${Math.round((option.probability || 0) * 100)}%`}
            >
              {Math.round((option.probability || 0) * 100)}%
            </button>
          ))}
        </div>
        
        {/* Range labels */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between",
          fontSize: 10,
          color: colors.textTer,
        }}>
          {options.map((option) => (
            <span key={option.id}>{option.option_text}</span>
          ))}
        </div>
      </div>
    );
  };

  const renderMarketContent = () => {
    switch (marketType) {
      case 'categorical':
        return renderCategoricalMarket();
      case 'range':
        return renderRangeMarket();
      default:
        return renderBinaryMarket();
    }
  };

  // Calculate days remaining
  const daysLeft = Math.max(0, Math.ceil((new Date(market.end_date) - new Date()) / 86400000));

  return (
    <div
      style={{
        background: colors.card,
        borderRadius: 12,
        padding: isMobileDevice ? 14 : 18,
        border: `1px solid ${colors.border}`,
        marginBottom: 12,
        cursor: "pointer",
      }}
      onClick={onViewDetails}
    >
      {/* Header */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
          {/* Category badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 8px",
              borderRadius: 4,
              background: `${getCategoryColor(market.category)}15`,
              color: getCategoryColor(market.category),
              fontSize: 11,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            <span>{getCategoryIcon(market.category)}</span>
            <span>{market.category.toUpperCase()}</span>
          </div>

          {/* Market type indicator */}
          {marketType !== 'binary' && (
            <div
              style={{
                padding: "2px 6px",
                borderRadius: 3,
                background: colors.blueBg,
                color: colors.blue,
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              {marketType === 'categorical' ? 'Много избора' : 'Диапазон'}
            </div>
          )}

          {/* Hot/Trending indicators */}
          {market.is_hot === 1 && (
            <span style={{ fontSize: 12 }}>🔥</span>
          )}
          {market.is_trending === 1 && (
            <span style={{ fontSize: 12 }}>📈</span>
          )}
        </div>

        {/* Question */}
        <h3
          style={{
            margin: 0,
            fontSize: isMobileDevice ? 15 : 16,
            fontWeight: 600,
            lineHeight: 1.3,
            color: colors.text,
          }}
        >
          {market.question}
        </h3>
      </div>

      {/* Voting interface */}
      <div 
        style={{ marginBottom: 12 }}
        onClick={(e) => e.stopPropagation()}
      >
        {renderMarketContent()}
      </div>

      {/* Footer */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        fontSize: 12,
        color: colors.textSec,
      }}>
        <span>
          {market.volume} гласа
          {isResolved ? (
            <span style={{ color: colors.green, fontWeight: 600, marginLeft: 8 }}>
              РЕШЕНА
            </span>
          ) : (
            <span> • {daysLeft} дни остават</span>
          )}
        </span>

        {hasVoted && !isResolved && (
          <span style={{
            color: colors.green,
            fontSize: 11,
            fontWeight: 600,
          }}>
            ✓ Гласувахте
          </span>
        )}
      </div>
    </div>
  );
}

export default EnhancedMarketRow;